import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type Lieferant, type WerLiefertWas } from '../lib/api';

// Lieferantenstamm (Einkauf E1) — Stammdaten mit Nutzungsstatistik aus dem Blechbedarf.

function fmtDatum(v?: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

// Lieferantenbewertung (E4): Kennzahlen aus 6 Jahren Einkaufshistorie.
// Termintreue = pünktliche Wareneingänge / Lieferungen mit auswertbaren Daten;
// Absagen aus dem Freitext "Lieferanten angefragt" der Historie geparst.
function BewertungSektion() {
  const bw = useApi(() => api.lieferanten.bewertung(), []);
  // Direktsprung von der Dashboard-Kachel (Ø Wiederbeschaffungszeit)
  useEffect(() => {
    if (window.location.hash === '#bewertung') {
      document.getElementById('bewertung')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [bw.loading]);
  const rows = bw.data?.data ?? [];
  const treue = (r: { termin_basis: number; puenktlich: number }) =>
    r.termin_basis >= 5 ? Math.round((r.puenktlich / r.termin_basis) * 100) : null;
  return (
    <div id="bewertung">
      <div className="mb-3">
        <h2 className="text-lg font-bold text-slate-900">Lieferantenbewertung</h2>
        <div className="text-xs text-slate-500">
          aus der Einkaufshistorie seit 2021 · Termintreue = pünktlich angekommene Lieferungen
          (nur Lieferanten mit ≥5 auswertbaren Lieferungen)
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[820px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium">Lieferant</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Käufe (12M)</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Käufe gesamt</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Termintreue</th>
              <th className="text-right px-2.5 py-1.5 font-medium" title="Ø Tage von Bestellung bis Wareneingang (Wiederbeschaffungszeit)">Ø Lieferzeit</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Ø Verzug</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Angefragt</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Absagen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bw.loading && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Lädt …</td></tr>}
            {rows.map(r => {
              const t = treue(r);
              return (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-2.5 py-1.5 font-medium">{r.name}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{r.kaeufe_12m}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{r.kaeufe}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {t == null ? <span className="text-slate-400">—</span> : (
                      <span className={`font-semibold ${t >= 90 ? 'text-emerald-700' : t >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                        {t} %
                      </span>
                    )}
                    {t != null && <span className="text-slate-400 text-xs ml-1">({r.puenktlich}/{r.termin_basis})</span>}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {r.lieferzeit_avg != null ? `${r.lieferzeit_avg} T` : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {r.verzug_avg != null ? `${r.verzug_avg} T` : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{r.angefragt}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {r.absagen}
                    {r.angefragt >= 5 && r.absagen > 0 && (
                      <span className="text-slate-400 text-xs ml-1">({Math.round((r.absagen / r.angefragt) * 100)} %)</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Produkt→Lieferant-Verzeichnis ("Wer liefert was?", Import aus Excel + eigene Einträge)
function WerLiefertWasSektion() {
  const wlw = useApi(() => api.einkauf.werLiefertWas(), []);
  const [q, setQ] = useState('');
  const [neu, setNeu] = useState<Partial<WerLiefertWas> | null>(null);
  const [busy, setBusy] = useState(false);
  const rows = (wlw.data?.data ?? []).filter((r: WerLiefertWas) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return [r.produkt, r.lieferant, r.ansprechpartner, r.bemerkung].some(v => (v ?? '').toLowerCase().includes(t));
  });

  const speichern = async () => {
    if (!neu?.produkt?.trim() || !neu?.lieferant?.trim()) return;
    setBusy(true);
    try {
      await api.einkauf.werLiefertWasAnlegen(neu);
      setNeu(null);
      wlw.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const loeschen = async (id: number) => {
    if (!confirm('Eintrag löschen?')) return;
    try { await api.einkauf.werLiefertWasLoeschen(id); wlw.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  return (
    <div className="mt-8">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Wer liefert was?</h2>
          <div className="text-xs text-slate-500">Produkt → Lieferant-Verzeichnis</div>
        </div>
        <div className="flex gap-2">
          <input placeholder="🔍 Produkt / Lieferant …" value={q} onChange={e => setQ(e.target.value)}
                 className="input min-w-[240px]" />
          <button onClick={() => setNeu({})} className="bg-brand text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 whitespace-nowrap">
            + Neuer Eintrag
          </button>
        </div>
      </div>
      {neu && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3 grid grid-cols-2 md:grid-cols-3 gap-2">
          <input className="input" placeholder="Produkt *" value={neu.produkt ?? ''} onChange={e => setNeu({ ...neu, produkt: e.target.value })} autoFocus />
          <input className="input" placeholder="Lieferant *" value={neu.lieferant ?? ''} onChange={e => setNeu({ ...neu, lieferant: e.target.value })} />
          <input className="input" placeholder="Ansprechpartner" value={neu.ansprechpartner ?? ''} onChange={e => setNeu({ ...neu, ansprechpartner: e.target.value })} />
          <input className="input" placeholder="Telefon" value={neu.telefon ?? ''} onChange={e => setNeu({ ...neu, telefon: e.target.value })} />
          <input className="input" placeholder="E-Mail" value={neu.email ?? ''} onChange={e => setNeu({ ...neu, email: e.target.value })} />
          <input className="input" placeholder="Bemerkung" value={neu.bemerkung ?? ''} onChange={e => setNeu({ ...neu, bemerkung: e.target.value })} />
          <div className="col-span-full flex gap-2 justify-end">
            <button onClick={() => setNeu(null)} className="btn-secondary">Abbrechen</button>
            <button onClick={() => void speichern()} disabled={busy || !neu.produkt?.trim() || !neu.lieferant?.trim()}
                    className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-40">
              {busy ? 'Speichert …' : 'Speichern'}
            </button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[860px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              {['Produkt','Lieferant','Ansprechpartner','Telefon','E-Mail','Bemerkung',''].map(h => (
                <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r: WerLiefertWas, i: number) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-2.5 py-1.5 font-medium whitespace-nowrap">
                  {i === 0 || rows[i - 1]?.produkt !== r.produkt ? r.produkt : ''}
                </td>
                <td className="px-2.5 py-1.5">{r.lieferant ?? '—'}</td>
                <td className="px-2.5 py-1.5">{r.ansprechpartner ?? '—'}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.telefon ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[240px] truncate" title={r.email ?? ''}>{r.email ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[260px] truncate" title={r.bemerkung ?? ''}>{r.bemerkung ?? '—'}</td>
                <td className="px-2.5 py-1.5 text-right">
                  <button onClick={() => void loeschen(r.id)} className="text-slate-400 hover:text-red-600 text-xs">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Lieferanten() {
  const list = useApi(() => api.lieferanten.list(), []);
  const [modal, setModal] = useState<Partial<Lieferant> | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!modal) return;
    if (!modal.name?.trim()) { alert('Name fehlt'); return; }
    setBusy(true);
    try {
      if (modal.id) await api.lieferanten.update(modal.id, modal);
      else          await api.lieferanten.create(modal);
      setModal(null); list.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  async function remove() {
    if (!modal?.id) return;
    if (!confirm(`Lieferant "${modal.name}" wirklich löschen?`)) return;
    setBusy(true);
    try { await api.lieferanten.remove(modal.id); setModal(null); list.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  const set = (k: keyof Lieferant, v: string | number | null) => setModal(m => ({ ...m, [k]: v }));

  return (
    <div className="p-4 md:p-8">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
          <h1 className="text-2xl font-bold text-slate-900">Lieferanten</h1>
        </div>
        <button onClick={() => setModal({ aktiv: 1 })}
                className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90">
          + Neuer Lieferant
        </button>
      </header>

      <BewertungSektion />

      <div className="mt-8 mb-3">
        <h2 className="text-lg font-bold text-slate-900">Lieferantenstamm</h2>
        <div className="text-xs text-slate-500">Stammdaten & Kontakte · Zeile anklicken zum Bearbeiten</div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[860px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              {['Name','Ansprechpartner','E-Mail','Telefon','Notiz','Käufe','Letzter Kauf','Status'].map(h => (
                <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.loading && <tr><td colSpan={8} className="text-center py-12 text-slate-500">Lädt …</td></tr>}
            {list.data?.data.map(l => (
              <tr key={l.id} onClick={() => setModal({ ...l })} className="hover:bg-slate-50 cursor-pointer">
                <td className="px-2.5 py-1.5 font-medium">{l.name}</td>
                <td className="px-2.5 py-1.5">{l.ansprechpartner ?? '—'}</td>
                <td className="px-2.5 py-1.5">{l.email ?? '—'}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{l.telefon ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[220px] truncate" title={l.notiz ?? ''}>{l.notiz ?? '—'}</td>
                <td className="px-2.5 py-1.5 text-right">{l.kaeufe ?? 0}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{fmtDatum(l.letzter_kauf)}</td>
                <td className="px-2.5 py-1.5">
                  {l.aktiv
                    ? <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold">aktiv</span>
                    : <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-xs font-semibold">inaktiv</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <WerLiefertWasSektion />

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl my-8" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">{modal.id ? `${modal.name} bearbeiten` : 'Neuer Lieferant'}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Name *</label>
                <input className="input w-full" value={modal.name ?? ''} onChange={e => set('name', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Ansprechpartner</label>
                <input className="input w-full" value={modal.ansprechpartner ?? ''} onChange={e => set('ansprechpartner', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Telefon</label>
                <input className="input w-full" value={modal.telefon ?? ''} onChange={e => set('telefon', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">E-Mail</label>
                <input type="email" className="input w-full" value={modal.email ?? ''} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Notiz</label>
                <textarea className="input w-full" rows={2} value={modal.notiz ?? ''} onChange={e => set('notiz', e.target.value)} />
              </div>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={!!modal.aktiv} onChange={e => set('aktiv', e.target.checked ? 1 : 0)} /> aktiv
              </label>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div>{modal.id && (
                <button onClick={remove} disabled={busy} className="text-red-600 text-sm hover:underline disabled:opacity-50">Löschen</button>
              )}</div>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="btn-secondary">Abbrechen</button>
                <button onClick={save} disabled={busy}
                        className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                  {busy ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
